import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { type TeamInvite } from '@this+that/models';
import { KeyboardKey } from '@this+that/utils';

import {
  type TeamHubCard,
  useTeamHub,
} from '../../../pages/private/Team/hooks/useTeamHub';
import { APP_PAGES } from '../../../routes/constants';
import { useTeamStore } from '../../../store/Team/useTeamStore';

export interface SidebarTeamSwitcherState {
  isOpen: boolean;
  isLoading: boolean;
  activeTeamId: string | null;
  activeTeamName: string;
  activeTeamColor: string | undefined;
  teams: TeamHubCard[];
  pendingInvites: TeamInvite[];
  isStatic: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  handleToggle: () => void;
  handleSelectTeam: (teamId: string) => void;
  handleManageTeams: () => void;
  handlePendingInvitesClick: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

export function useSidebarTeamSwitcher(): SidebarTeamSwitcherState {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasLoadedOnceRef = useRef(false);

  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const teamsById = useTeamStore((s) => s.teamsById);
  const activeTeam = teamsById[activeTeamId ?? ''];
  const userTeamIds = useTeamStore((s) => s.userTeamIds);
  const userTeamIdsLength = userTeamIds?.length ?? 0;

  const { teams, pendingInvites, ensureTeamsLoaded } = useTeamHub({
    autoLoad: false,
  });

  const activeTeamName = activeTeam?.team.name ?? activeTeamId ?? '';
  const activeTeamColor = activeTeam?.team.hexColor;
  const isStatic = userTeamIdsLength <= 1 && pendingInvites?.length === 0;
  const isLoading =
    userTeamIds === null || (userTeamIdsLength > 0 && !activeTeam);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const handleToggle = useCallback(() => {
    if (isStatic) {
      return;
    }

    setDropdownOpen((prev) => {
      const nextOpen = !prev;
      if (nextOpen && !hasLoadedOnceRef.current) {
        hasLoadedOnceRef.current = true;
        ensureTeamsLoaded();
      }
      return nextOpen;
    });
  }, [isStatic, ensureTeamsLoaded]);

  const handleSelectTeam = useCallback((teamId: string) => {
    useTeamStore.getState().setActiveTeamId(teamId);
    setDropdownOpen(false);
  }, []);

  const handleManageTeams = useCallback(() => {
    navigate(APP_PAGES.TEAM);
    setDropdownOpen(false);
  }, [navigate]);

  const handlePendingInvitesClick = useCallback(() => {
    navigate(APP_PAGES.TEAM);
    setDropdownOpen(false);
  }, [navigate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === KeyboardKey.ESCAPE) {
      setDropdownOpen(false);
    }
  }, []);

  return {
    isOpen: dropdownOpen,
    isLoading,
    activeTeamId,
    activeTeamName,
    activeTeamColor,
    teams,
    pendingInvites,
    isStatic,
    containerRef,
    handleToggle,
    handleSelectTeam,
    handleManageTeams,
    handlePendingInvitesClick,
    handleKeyDown,
  };
}
